import type { AutomationSettings } from "@repo/app-types/entities/automation";
import { applyAutomationConfigurationProposal } from "@repo/app-types/entities/automationProposal";
import type { CopilotProposal } from "@repo/app-types/entities/copilotProposal";
import { FieldError } from "@repo/ui/field/fieldError";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { useEffect, useRef, useState, type FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { useUpdateAutomationMutation } from "../../api/entities/automation.js";
import { useResolveProposalMutation } from "../../api/entities/copilotProposal.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { ResourceSaveFooter } from "../../components/resourceSaveFooter.js";
import { AutomationConfigForm } from "../../features/automations/automationConfigForm.js";
import { CoachingPlaybookConflictModal } from "../../features/automations/coachingPlaybookConflictModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";
import { useDeepLinkedProposal } from "../../features/researchAgent/copilotDeepLinks.js";
import { useReportEditorProposal } from "../../features/researchAgent/editorProposalsSlice.js";
import { checkIsAutomationConfigDirty } from "./automationConfigDirty.js";
import { useAutomationDetailOutletContext } from "./automationDetailPage.js";

export const AutomationOverviewTab: FC = () => {
  const { automation, reportHasUnsavedChanges } =
    useAutomationDetailOutletContext();

  const {
    isReadOnly,
    playbookAndVoiceAgentOptions,
    arePlaybooksLoading,
    hasLoadedAllPlaybookOptions,
    areRecipientOptionsLoading,
    teammateOptions,
    editorAndReviewerTeammateOptions,
    groupOptions,
    managerTeammateIds,
    coachingPlaybookIdsInUse,
  } = useAutomationFieldsContext();
  const [updateAutomation, { isLoading: isUpdating }] =
    useUpdateAutomationMutation();
  const [resolveProposal] = useResolveProposalMutation();

  const [name, setName] = useState(automation.name);
  const [settings, setSettings] = useState<AutomationSettings>(
    automation.settings,
  );
  // The proposal in the draft, if any. Save applies it.
  const [linkedProposal, setLinkedProposal] =
    useState<CopilotProposal<"automation"> | null>(null);
  const [hasCoachingConflict, setHasCoachingConflict] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A unique id for this editing session, generated when the config screen
  // loads. Stamped on every revision so revisions can later be grouped by
  // session and conflicting edits detected. Not surfaced to users yet.
  const sessionIdRef = useRef(uuidv4());

  // Compare the normalized value the server stores so whitespace-only edits do
  // not keep the explicit-save footer visible.
  const effectiveName = name.trim() || automation.name;

  const isDirty = checkIsAutomationConfigDirty(
    { name: effectiveName, settings },
    { name: automation.name, settings: automation.settings },
  );

  // A suggestion is compiled onto the draft, keeping the user's other unsaved
  // edits. It stays pending until Save writes the draft, which applies it.
  const putProposalIntoDraft = useEffectEvent(
    (
      proposal: CopilotProposal<"automation"> & {
        data: { kind: "update_automation" };
      },
    ) => {
      try {
        const configured = applyAutomationConfigurationProposal(
          { name, settings },
          proposal.data.configuration,
          { managerTeammateIds },
        );
        setName(configured.name);
        setSettings(configured.settings);
        setLinkedProposal(proposal);
        setSaveError(null);
      } catch {
        setSaveError("This suggestion no longer fits this automation.");
      }
    },
  );

  // One-shot link from an Ask Alpharun proposal card (?proposalId=...): the
  // proposal goes into the draft as soon as it loads. A proposal for another
  // automation loads nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("automation", {
      onUnavailable: () =>
        setSaveError("This suggestion is no longer available."),
    });
  useEffect(() => {
    // A people change compiles against who manages a group, so it waits for
    // the groups to load.
    if (areRecipientOptionsLoading || !deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    const { data } = deepLinkProposal;
    if (
      deepLinkProposal.resource_id === automation.id &&
      data.kind === "update_automation"
    ) {
      putProposalIntoDraft({ ...deepLinkProposal, data });
    }
  }, [
    areRecipientOptionsLoading,
    deepLinkProposal,
    clearDeepLinkProposalId,
    automation.id,
    putProposalIntoDraft,
  ]);

  useReportEditorProposal(linkedProposal?.id, isUpdating);

  // Save writes the draft when it differs from the automation, then applies
  // the suggestion in it. A clean draft with a suggestion still linked only
  // applies it.
  const handleSave = async () => {
    setSaveError(null);
    const savedProposal = linkedProposal;
    if (isDirty) {
      try {
        await updateAutomation({
          id: automation.id,
          name: effectiveName,
          settings,
          session_id: sessionIdRef.current,
        }).unwrap();
      } catch (error) {
        if (isConflictQueryError(error)) {
          setSettings(automation.settings);
          setName(automation.name);
          setLinkedProposal(null);
          setHasCoachingConflict(true);
          return;
        }
        setSaveError("Your changes couldn't be saved. Please try again.");
        return;
      }
    }
    // The saved draft carried the suggestion, so it is applied. One that has
    // moved on (409) was already decided; the save stands either way. Any other
    // failure keeps the suggestion linked, so the next Save retries it.
    // TODO: Apply the proposal in the save's transaction on the server instead.
    if (savedProposal) {
      try {
        await resolveProposal({
          proposalId: savedProposal.id,
          status: "applied",
        }).unwrap();
      } catch (error) {
        if (!isConflictQueryError(error)) {
          setSaveError(
            "The automation was saved, but the suggestion could not be marked as applied.",
          );
          return;
        }
      }
      setLinkedProposal((current) =>
        current === savedProposal ? null : current,
      );
    }
  };

  const handleCancel = () => {
    setName(automation.name);
    setSettings(automation.settings);
    setLinkedProposal(null);
    setSaveError(null);
  };

  useEffect(() => {
    reportHasUnsavedChanges(isDirty);
    return () => {
      reportHasUnsavedChanges(false);
    };
  }, [isDirty, reportHasUnsavedChanges]);

  return (
    <>
      <AutomationConfigForm
        templateId={automation.template_id}
        isReadOnly={isReadOnly}
        playbookAndVoiceAgentOptions={playbookAndVoiceAgentOptions}
        arePlaybooksLoading={arePlaybooksLoading}
        hasLoadedAllPlaybookOptions={hasLoadedAllPlaybookOptions}
        areRecipientOptionsLoading={areRecipientOptionsLoading}
        teammateOptions={teammateOptions}
        editorAndReviewerTeammateOptions={editorAndReviewerTeammateOptions}
        groupOptions={groupOptions}
        managerTeammateIds={managerTeammateIds}
        coachingPlaybookIdsInUse={coachingPlaybookIdsInUse}
        name={name}
        settings={settings}
        savedConfiguration={automation}
        onNameChange={setName}
        onSettingsChange={setSettings}
        onBlur={() => {
          setName(effectiveName);
        }}
      />

      {saveError ? <FieldError>{saveError}</FieldError> : null}

      {isDirty || linkedProposal ? (
        <ResourceSaveFooter
          className="sticky bottom-0 z-10 -mx-4 mt-auto -mb-4 sm:-mx-6 sm:-mb-6"
          onCancel={handleCancel}
          onSave={() => {
            void handleSave();
          }}
          isSaving={isUpdating}
        />
      ) : null}

      <CoachingPlaybookConflictModal
        isOpen={hasCoachingConflict}
        onClose={() => {
          setHasCoachingConflict(false);
        }}
      />
    </>
  );
};
